import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import "../../styles/admin/admin-dashboard.css";
import "../../styles/admin/admin-form.css";
import config from "../../config";

const API_BASE_URL = String(config.apiBaseUrl || "").replace(/\/+$/, "");

function MainHeroCard({ activeUsers, todayRegistrations, monthRegistrations }) {
  const navigate = useNavigate();

  return (
    <section className="dashboard-hero-card">
      <div className="dashboard-hero-content">
        <span className="dashboard-hero-badge">Resumen general</span>

        <span className="dashboard-hero-label">Usuarios activos</span>
        <h2>{activeUsers}</h2>

        <button
          className="dashboard-hero-btn"
          type="button"
          onClick={() => navigate("/admin/usuarios")}
        >
          Ver datos completos
          <ArrowRight size={18} />
        </button>
      </div>

      <div className="dashboard-hero-visual">
        <div className="hero-circle hero-circle-one"></div>
        <div className="hero-circle hero-circle-two"></div>

        <div className="hero-card-mini">
          <span>Registros hoy</span>
          <strong>{todayRegistrations}</strong>
        </div>

        <div className="hero-card-mini second">
          <span>Registros este mes</span>
          <strong>{monthRegistrations}</strong>
        </div>
      </div>
    </section>
  );
}

function SideScoreCard({ total, dogs, cats }) {
  const dogPercent = total > 0 ? Math.round((dogs / total) * 100) : 0;
  const catPercent = total > 0 ? 100 - dogPercent : 0;

  return (
    <section className="dashboard-score-card">
      <div className="score-top">
        <span>Mascotas registradas</span>
        <strong>{total}</strong>
      </div>

      <div className="score-gauge">
        <div
          className="score-gauge-ring"
          style={{
            background: `conic-gradient(
              #cab19c 0deg ${dogPercent * 3.6}deg,
              #2e5e71 ${dogPercent * 3.6}deg 360deg
            )`,
          }}
        >
          <div className="score-gauge-inner"></div>
        </div>
      </div>

      <div className="score-legend">
        <div className="legend-item">
          <span className="legend-dot dog"></span>
          <span>Perros {dogPercent}%</span>
        </div>

        <div className="legend-item">
          <span className="legend-dot cat"></span>
          <span>Gatos {catPercent}%</span>
        </div>
      </div>
    </section>
  );
}

function AlliesSummaryCard({ totalAllies, recentAllies }) {
  const navigate = useNavigate();

  return (
    <section className="dashboard-bottom-card allies-summary-card">
      <div className="allies-summary-top">
        <div>
          <h3>Total aliados</h3>
          <strong className="allies-total-number">{totalAllies}</strong>
        </div>

        <button
          type="button"
          className="allies-total-btn"
          onClick={() => navigate("/admin/aliados")}
        >
          Ver total de aliados
        </button>
      </div>

      <div className="allies-list">
        {recentAllies.length > 0 ? (
          recentAllies.map((ally) => {
            const fullName = `${ally.name || ""} ${ally.lastName || ""}`.trim();

            return (
              <div className="ally-item" key={ally.idUser}>
                <div className="ally-avatar">{fullName.charAt(0) || "A"}</div>
                <div className="ally-text">
                  <strong>{fullName || "Aliado"}</strong>
                  <span>{ally.email || "Añadido recientemente"}</span>
                </div>
              </div>
            );
          })
        ) : (
          <div className="ally-item">
            <div className="ally-text">
              <strong>No hay aliados recientes</strong>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

// =====================================================
// PRUEBA GRATIS VS PAGANDO (gráfica de barras)
// =====================================================
function PlanUsersChartCard({ trialUsers, payingUsers, loading, error }) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (loading || error) {
      setReady(false);
      return undefined;
    }

    // Doble frame para que el navegador pinte las barras en 0
    // antes de animarlas hasta su altura real.
    let secondFrame;
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => setReady(true));
    });

    return () => {
      cancelAnimationFrame(firstFrame);
      if (secondFrame) cancelAnimationFrame(secondFrame);
    };
  }, [loading, error]);

  const total = trialUsers + payingUsers;
  const highest = Math.max(trialUsers, payingUsers);
  const step = Math.max(1, Math.ceil(highest / 4));
  const scaleMax = step * 4;
  const ticks = [0, 1, 2, 3, 4].map((i) => i * step);
  const payingPercent = total > 0 ? Math.round((payingUsers / total) * 100) : 0;

  const bars = [
    {
      key: "trial",
      label: "Prueba gratis",
      detail: "7 días",
      value: trialUsers,
    },
    {
      key: "paying",
      label: "Pagando",
      detail: "Plan Pro",
      value: payingUsers,
    },
  ];

  return (
    <section
      className={`dashboard-bottom-card plan-chart-card ${ready ? "is-ready" : ""}`}
    >
      <div className="plan-chart-head">
        <h3>Prueba gratis y pagos</h3>
        <p className="plan-chart-subtitle">
          Personas en la prueba de 7 días frente a quienes pagan el plan Pro
        </p>
      </div>

      {loading ? (
        <p className="plan-chart-status">Cargando usuarios...</p>
      ) : error ? (
        <p className="plan-chart-status error">
          No se pudieron cargar los usuarios. Recarga la página para intentarlo de nuevo.
        </p>
      ) : (
        <>
          <div
            className="plan-chart"
            role="img"
            aria-label={`Prueba gratis: ${trialUsers} personas. Pagando: ${payingUsers} personas.`}
          >
            <div className="plan-chart-plot">
              {ticks.map((tick) => (
                <div
                  key={tick}
                  className="plan-chart-gridline"
                  style={{ bottom: `${(tick / scaleMax) * 100}%` }}
                >
                  <span>{tick}</span>
                </div>
              ))}

              <div className="plan-chart-bars">
                {bars.map((bar) => {
                  const percent = (bar.value / scaleMax) * 100;

                  return (
                    <div className="plan-chart-column" key={bar.key}>
                      <div
                        className={`plan-chart-bar ${bar.key}`}
                        style={{
                          height: ready ? `max(8px, ${percent}%)` : "0%",
                        }}
                      >
                        <span className="plan-chart-value">{bar.value}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="plan-chart-labels">
              {bars.map((bar) => (
                <div className="plan-chart-label" key={bar.key}>
                  <strong>{bar.label}</strong>
                  <span>{bar.detail}</span>
                </div>
              ))}
            </div>
          </div>

          <p className="plan-chart-summary">
            {total > 0
              ? `${payingPercent}% de las ${total} personas con plan ya están pagando.`
              : "Todavía no hay personas en prueba ni pagando."}
          </p>
        </>
      )}
    </section>
  );
}

export default function AdminDashboard() {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Usuarios para la gráfica de prueba gratis vs pagando.
  // Se cargan aparte para que un fallo aquí no tumbe el dashboard.
  const [planUsers, setPlanUsers] = useState([]);
  const [planLoading, setPlanLoading] = useState(true);
  const [planError, setPlanError] = useState("");

  useEffect(() => {
    loadDashboard();
    loadPlanUsers();
  }, []);

  const loadDashboard = async () => {
    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("token");

      if (!token) {
        throw new Error("No hay sesión activa. Inicia sesión nuevamente.");
      }

      const response = await fetch(`${API_BASE_URL}/api/admin/dashboard`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const responseText = await response.text();

      if (!response.ok) {
        throw new Error(responseText || "No se pudo cargar el dashboard");
      }

      const data = responseText ? JSON.parse(responseText) : null;
      setDashboard(data);
    } catch (err) {
      setError(err.message || "Ocurrió un error al cargar el dashboard");
      setDashboard(null);
    } finally {
      setLoading(false);
    }
  };

  const loadPlanUsers = async () => {
    setPlanLoading(true);
    setPlanError("");

    try {
      const token = localStorage.getItem("token");

      if (!token) {
        throw new Error("No hay sesión activa. Inicia sesión nuevamente.");
      }

      const response = await fetch(`${API_BASE_URL}/api/admin/users`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const responseText = await response.text();

      if (!response.ok) {
        throw new Error(responseText || "No se pudo cargar la lista de usuarios");
      }

      const data = responseText ? JSON.parse(responseText) : [];
      setPlanUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      setPlanError(err.message || "Ocurrió un error al cargar los usuarios");
      setPlanUsers([]);
    } finally {
      setPlanLoading(false);
    }
  };

  const planStats = useMemo(() => {
    let trialUsers = 0;
    let payingUsers = 0;

    planUsers.forEach((user) => {
      const status = String(user.status || "").trim().toUpperCase();
      const isAlly =
        String(user.userType || "").trim().toUpperCase() === "ALLY" ||
        user.billingExempt === true;

      // Los aliados no pagan, así que no cuentan en ninguna barra
      if (isAlly) return;

      if (status === "TRIAL_ACTIVE") {
        trialUsers += 1;
      } else if (status === "ACTIVE") {
        payingUsers += 1;
      }
    });

    return { trialUsers, payingUsers };
  }, [planUsers]);

  if (loading) {
    return (
      <div className="admin-page">
        <p>Cargando dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-page">
        <p className="admin-form-message error">{error}</p>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="admin-page">
        <p>No se pudo cargar la información del dashboard.</p>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="dashboard-top-grid">
        <MainHeroCard
          activeUsers={dashboard.activeUsers || 0}
          todayRegistrations={dashboard.todayRegistrations || 0}
          monthRegistrations={dashboard.monthRegistrations || 0}
        />

        <SideScoreCard
          total={dashboard.totalPets || 0}
          dogs={dashboard.dogs || 0}
          cats={dashboard.cats || 0}
        />
      </div>

      <div className="dashboard-bottom-grid dashboard-bottom-grid-custom">
        <AlliesSummaryCard
          totalAllies={dashboard.totalAllies || 0}
          recentAllies={dashboard.recentAllies || []}
        />

        <PlanUsersChartCard
          trialUsers={planStats.trialUsers}
          payingUsers={planStats.payingUsers}
          loading={planLoading}
          error={planError}
        />
      </div>
    </div>
  );
}